import { useMemo } from 'react'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import useInvestAssets from 'src/common/apollo/hooks/useInvestAssets'
import useLockedAmountsInOTC from 'src/common/apollo/hooks/useLockedAmountsInOTC'
import { OfferStatus } from 'src/common/apollo/types/swarmx/graphql'
import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { getPriceFromPriceFeed } from 'src/common/queries/usePriceFeeds/utils'
import { big, ZERO } from 'src/common/utils/big-utils'
import { unifyAddress } from 'src/common/utils/web3/unifyAddress'
import useConfig from 'src/config/hooks/useConfig'

const useLockedBalance = () => {
  const { tokensAddresses } = useConfig()

  const { smartAccountInfo } = useSmartAccount()
  const { priceFeeds, isLoading: priceFeedsLoading } = useAllPriceFeeds()

  const {
    tokensWithLockedAmountDictionary,
    loading: lockedAmountsLoading,
    refetch: lockedAmountRefetch,
  } = useLockedAmountsInOTC({
    variables: {
      filter: {
        maker_contains_nocase: smartAccountInfo?.address,
        offerStatus_not_in: [OfferStatus.Cancelled, OfferStatus.FullyTaken],
      },
    },
  })

  const {
    investAssets,
    loading: investAssetsLoading,
    refetch: investRefetch,
  } = useInvestAssets()

  const goldBalanceInUsd = useMemo(() => {
    const goldAddresses = [
      unifyAddress(tokensAddresses.goldKg || ''),
      unifyAddress(tokensAddresses.goldOz || ''),
      unifyAddress(tokensAddresses.xGold || ''),
    ]

    let sum = ZERO

    tokensWithLockedAmountDictionary.forEach(token => {
      if (goldAddresses.includes(unifyAddress(token.id))) {
        sum = sum.plus(token.lockedAmount)

        const exchangePrice = getPriceFromPriceFeed(
          priceFeeds,
          token.contractAddress
        )

        if (exchangePrice) {
          const balanceInUsd = big(token.lockedAmount).mul(exchangePrice)
          sum = sum.plus(balanceInUsd)
        }
      }
    })

    return sum
  }, [
    priceFeeds,
    tokensAddresses.goldKg,
    tokensAddresses.goldOz,
    tokensAddresses.xGold,
    tokensWithLockedAmountDictionary,
  ])

  const rwaBalanceInUsd = useMemo(() => {
    return investAssets.reduce((sum, token) => {
      if (tokensWithLockedAmountDictionary.has(token.contractAddress)) {
        const exchangePrice = getPriceFromPriceFeed(
          priceFeeds,
          token.contractAddress
        )

        if (exchangePrice) {
          const balanceInUsd = big(
            tokensWithLockedAmountDictionary.get(token.contractAddress)!
              .lockedAmount
          ).mul(exchangePrice)
          sum = sum.plus(balanceInUsd)
        }
      }
      return sum
    }, ZERO)
  }, [investAssets, priceFeeds, tokensWithLockedAmountDictionary])

  const cryptoBalanceInUsd = useMemo(() => {
    let sum = ZERO

    tokensWithLockedAmountDictionary.forEach(token => {
      const exchangePrice = getPriceFromPriceFeed(
        priceFeeds,
        token.contractAddress
      )
      if (exchangePrice) {
        const balanceInUsd = big(token.lockedAmount).mul(exchangePrice)
        sum = sum.plus(balanceInUsd)
      }
    }, ZERO)

    return sum.minus(rwaBalanceInUsd).minus(goldBalanceInUsd)
  }, [
    goldBalanceInUsd,
    priceFeeds,
    rwaBalanceInUsd,
    tokensWithLockedAmountDictionary,
  ])

  const totalBalanceInUsd = goldBalanceInUsd
    .plus(rwaBalanceInUsd)
    .plus(cryptoBalanceInUsd)

  const refetch = () => {
    return Promise.all([lockedAmountRefetch(), investRefetch()])
  }

  return {
    goldBalanceInUsd,
    rwaBalanceInUsd,
    cryptoBalanceInUsd,
    totalBalanceInUsd,
    loading: priceFeedsLoading || investAssetsLoading || lockedAmountsLoading,
    refetch,
  }
}

export default useLockedBalance
